import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { connection } from '@/lib/api';
import { useSetupStore } from '@/stores/setupStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useWriteEnableStore } from '@/stores/writeEnableStore';

type Status = Awaited<ReturnType<typeof connection.getStatus>>;
type Apps = Awaited<ReturnType<typeof connection.getAllOAuthSettings>>['settings'];
// account_id was added to oauth_app_settings (migration 85) but the generated
// client type lags until `encore gen client`. Extend locally so we can surface
// the S2S account_id now (it populates once Step-1 capture runs).
type AppRow = Apps[number] & { accountId?: string | null; appName?: string | null };

/**
 * Account Overview — which Zoom account ZAC is connected to and which
 * Server-to-Server apps have been provisioned for it. Read-only surface over
 * connection.getStatus() (the connected account identity) and
 * connection.getAllOAuthSettings() (the defined apps — secrets never leave the
 * backend; only the client_id is shown, masked).
 */
export function AccountPage() {
  const navigate = useNavigate();
  const setupConversationId = useSetupStore((s) => s.setupConversationId);
  // Send the user back into the guided Zoom-setup chat (the starred "Zoom
  // setup" conversation) to continue — fall back to a new chat if it doesn't
  // exist yet.
  const goToSetupChat = () => navigate(setupConversationId ? `/chat/${setupConversationId}` : '/chat');
  const [status, setStatus] = useState<Status | null>(null);
  const [apps, setApps] = useState<Apps>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Write-access state (enabled from here, a report Fix or a chat change; disabled here).
  const capabilities = useConnectionStore((s) => s.capabilities);
  const fetchCapabilities = useConnectionStore((s) => s.fetchCapabilities);
  const disableWrite = useConnectionStore((s) => s.disableWrite);
  const [writeBusy, setWriteBusy] = useState(false);
  useEffect(() => { void fetchCapabilities(); }, [fetchCapabilities]);
  const onDisableWrite = async () => {
    setWriteBusy(true);
    await disableWrite();
    setWriteBusy(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, a] = await Promise.all([
          connection.getStatus(),
          connection.getAllOAuthSettings(),
        ]);
        if (cancelled) return;
        setStatus(s);
        setApps(a.settings ?? []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load account overview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const conn = status?.connection;
  // Connection-app readiness. Our guided flow creates the S2S app but stops at
  // Zoom's "Add Scopes" step, so a freshly-provisioned app has NO granted
  // scopes yet — ZAC can't read config until they're added. We surface that as
  // a distinct state. Signal = the connection's granted scopes (empty =
  // pending). NOTE: scopes live on the connection, not per-app; fine while
  // there's a single connection app. When we later capture granted scopes this
  // flips to "ready" automatically.
  const scopeCount = conn?.scopes?.length ?? 0;
  const scopesReady = scopeCount > 0;
  const maskId = (id: string) => (id.length > 4 ? `••••••••${id.slice(-4)}` : id);
  const purposeLabel = (p: string) => (p === 'reader' ? 'Read-only' : p === 'writer' ? 'Read + write' : p);
  const fmtDate = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <h1 className="text-2xl font-semibold text-foreground">Account</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Your Zoom connection and the apps ZAC has provisioned on your account.
      </p>

      {loading && <p className="mt-8 text-sm text-muted-foreground">Loading…</p>}
      {error && !loading && (
        <div className="mt-6 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Connected Zoom account */}
          <section className="mt-6 rounded-xl border border-border bg-card p-5">
            <h2 className="text-sm font-medium text-foreground">Signed-in Zoom account</h2>
            {status?.connected && conn ? (
              <dl className="mt-3 grid grid-cols-[140px_1fr] gap-y-2 text-sm">
                <dt className="text-muted-foreground">Account</dt>
                <dd className="text-foreground">{conn.accountName || '—'}</dd>
                <dt className="text-muted-foreground">Account ID</dt>
                <dd className="font-mono text-foreground">{conn.accountId || '—'}</dd>
                <dt className="text-muted-foreground">Connected</dt>
                <dd className="text-foreground">{fmtDate(conn.connectedAt)}</dd>
                <dt className="text-muted-foreground">Status</dt>
                <dd><span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400">● Connected</span></dd>
              </dl>
            ) : (
              <div className="mt-3">
                <p className="text-sm text-muted-foreground">No Zoom account connected yet.</p>
                <button
                  onClick={goToSetupChat}
                  className="mt-3 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                  Run Zoom setup
                </button>
              </div>
            )}
          </section>

          {/* Write access */}
          {status?.connected && (
            <section className="mt-5 rounded-xl border border-border bg-card p-5">
              <h2 className="text-sm font-medium text-foreground">Write access</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Lets ZAC apply the changes you ask for — in chat, from the report's fixes, or a revert. When off, ZAC reads your settings and holds any change until you enable it.
              </p>
              <div className="mt-3 flex items-center justify-between">
                {capabilities?.canWrite ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400">● Enabled</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground">Disabled</span>
                )}
                {capabilities?.canWrite ? (
                  <button
                    onClick={onDisableWrite}
                    disabled={writeBusy}
                    className="rounded-md border border-border bg-secondary px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-secondary/70 disabled:opacity-60"
                  >
                    {writeBusy ? 'Disabling…' : 'Disable write access'}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      // A fresh enablement from here carries no plan to hand back to.
                      useWriteEnableStore.getState().clearPendingPlan();
                      navigate('/chat?setup=write');
                    }}
                    className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
                  >
                    Enable write access
                  </button>
                )}
              </div>
              {!capabilities?.canWrite && (
                <p className="mt-2 text-xs text-muted-foreground">ZAC adds the write scopes to your connection app in the guided browser — you'll see it happen.</p>
              )}
            </section>
          )}

          {/* ZAC Connection App */}
          <section className="mt-5 rounded-xl border border-border bg-card p-5">
            <h2 className="text-sm font-medium text-foreground">ZAC Connection App</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              The Server-to-Server app ZAC provisioned on your Zoom account to access its APIs. The client secret is stored encrypted and never displayed.
            </p>
            {apps.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No connection app configured yet.</p>
            ) : (
              <ul className="mt-4 space-y-4">
                {(apps as AppRow[]).map((app) => (
                  <li key={app.id} className="rounded-lg border border-border/60 bg-background/40 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-1">
                        <span className="text-base font-semibold text-foreground">{app.appName || 'Unnamed app'}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Server-to-Server OAuth</span>
                          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
                            {purposeLabel(app.appPurpose)}
                          </span>
                        </div>
                      </div>
                      {scopesReady ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-medium text-emerald-400">● Ready</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-medium text-amber-400">▲ Scopes pending</span>
                      )}
                    </div>

                    {/* Lifecycle state — created in Zoom vs. fully scoped/ready. */}
                    <div className="mt-4 rounded-lg border border-border/60 bg-background/60 p-3">
                      <ol className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/20 text-[10px] text-emerald-400">✓</span>
                          <span className="text-foreground">Created on your Zoom account</span>
                        </li>
                        <li className="flex items-center gap-2">
                          {scopesReady ? (
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/20 text-[10px] text-emerald-400">✓</span>
                          ) : (
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-amber-500/20 text-[10px] text-amber-400">…</span>
                          )}
                          <span className={scopesReady ? 'text-foreground' : 'text-muted-foreground'}>
                            Read scopes granted{scopesReady ? ` (${scopeCount})` : ''}
                          </span>
                        </li>
                      </ol>
                      {!scopesReady && (
                        <div className="mt-3 flex items-center justify-between gap-3 border-t border-border/50 pt-3">
                          <p className="text-xs text-amber-400/90">
                            The app exists on Zoom but its read scopes haven't been granted yet — ZAC can't read your configuration until they're added.
                          </p>
                          <button
                            onClick={goToSetupChat}
                            className="shrink-0 rounded-lg bg-amber-500/90 px-3 py-1.5 text-xs font-medium text-black hover:bg-amber-500"
                          >
                            Continue with setup
                          </button>
                        </div>
                      )}
                    </div>
                    <dl className="mt-4 grid grid-cols-[150px_1fr] gap-x-4 gap-y-2 text-sm">
                      <dt className="text-muted-foreground">Account ID</dt>
                      <dd className="font-mono text-foreground">{app.accountId || <span className="text-muted-foreground">—</span>}</dd>

                      <dt className="text-muted-foreground">Client ID</dt>
                      <dd className="font-mono text-foreground">{maskId(app.clientId)}</dd>

                      <dt className="text-muted-foreground">Client Secret</dt>
                      <dd className="text-foreground">•••••••• <span className="text-xs text-muted-foreground">(stored encrypted, never displayed)</span></dd>

                      <dt className="text-muted-foreground">Access</dt>
                      <dd className="text-foreground">{purposeLabel(app.appPurpose)}</dd>

                      <dt className="text-muted-foreground">Redirect URI</dt>
                      <dd className="text-foreground">{app.redirectUri ? <span className="font-mono">{app.redirectUri}</span> : <span className="text-muted-foreground">Not used (Server-to-Server)</span>}</dd>

                      <dt className="text-muted-foreground">Created</dt>
                      <dd className="text-foreground">{fmtDate(app.createdAt)}</dd>

                      <dt className="text-muted-foreground">Last updated</dt>
                      <dd className="text-foreground">{fmtDate(app.updatedAt)}</dd>
                    </dl>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
